import React from "react";
import {
  OrganizationDataRecipientDto,
  ProcessingRegion,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { SimpleCheckbox } from "@truevault/aquilo";
import clsx from "clsx";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";

export function VendorRegionCheckbox({
  vendor,
  region,
  updateVendor,
  namePrefix = "",
  labelMarginClassName = "ml-[12px]",
}: {
  vendor: OrganizationDataRecipientDto;
  region: ProcessingRegion;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  namePrefix?: string;
  labelMarginClassName?: string;
}) {
  return (
    <SimpleCheckbox
      name={`${namePrefix}region-${region}`}
      label={
        <div className={clsx(labelMarginClassName, "flex items-center")}>
          <div>{ProcessingRegionToFlagIcon[region]}</div>
          <div
            className={clsx(
              "text-blue-800 text-[12px] font-medium ml-[8px]",
              "bg-blue-100 rounded-[10px] px-[10px] py-[2px] max-h-[20px]",
              "flex items-center justify-center",
            )}
          >
            {ProcessingRegionToCountryName[region]}
          </div>
        </div>
      }
      initialChecked={vendor.processingRegions.includes(region)}
      onChange={async (newChecked) => {
        let newRegions = vendor.processingRegions;
        if (newChecked && !newRegions.includes(region)) {
          newRegions.push(region);
        } else if (!newChecked && newRegions.includes(region)) {
          newRegions = newRegions.filter((r) => r !== region);
        }

        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: {
            ...vendor,
            processingRegions: newRegions,
          },
        });
      }}
    />
  );
}

export const USA_FLAG_ICON = (
  <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_172_9690)">
      <mask
        id="mask0_172_9690"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="21"
        height="16"
      >
        <path
          d="M19 0.608154H2C0.89543 0.608154 0 1.50358 0 2.60815V13.6082C0 14.7127 0.89543 15.6082 2 15.6082H19C20.1046 15.6082 21 14.7127 21 13.6082V2.60815C21 1.50358 20.1046 0.608154 19 0.608154Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_172_9690)">
        <path
          d="M19 0.608154H2C0.89543 0.608154 0 1.50358 0 2.60815V13.6082C0 14.7127 0.89543 15.6082 2 15.6082H19C20.1046 15.6082 21 14.7127 21 13.6082V2.60815C21 1.50358 20.1046 0.608154 19 0.608154Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 0.608154H9V7.60815H0V0.608154Z"
          fill="#444379"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1 1.60815V2.60815H2V1.60815H1ZM3 1.60815V2.60815H4V1.60815H3ZM5 1.60815V2.60815H6V1.60815H5ZM7 1.60815V2.60815H8V1.60815H7ZM6 2.60815V3.60815H7V2.60815H6ZM4 2.60815V3.60815H5V2.60815H4ZM2 2.60815V3.60815H3V2.60815H2ZM1 3.60815V4.60815H2V3.60815H1ZM3 3.60815V4.60815H4V3.60815H3ZM5 3.60815V4.60815H6V3.60815H5ZM7 3.60815V4.60815H8V3.60815H7ZM1 5.60815V6.60815H2V5.60815H1ZM3 5.60815V6.60815H4V5.60815H3ZM5 5.60815V6.60815H6V5.60815H5ZM7 5.60815V6.60815H8V5.60815H7ZM6 4.60815V5.60815H7V4.60815H6ZM4 4.60815V5.60815H5V4.60815H4ZM2 4.60815V5.60815H3V4.60815H2Z"
          fill="#A7B6E7"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9 0.608154V1.60815H21V0.608154H9ZM9 2.60815V3.60815H21V2.60815H9ZM9 4.60815V5.60815H21V4.60815H9ZM9 6.60815V7.60815H21V6.60815H9ZM0 8.60815V9.60815H21V8.60815H0ZM0 10.6082V11.6082H21V10.6082H0ZM0 12.6082V13.6082H21V12.6082H0ZM0 14.6082V15.6082H21V14.6082H0Z"
          fill="#ED4C49"
        />
        <path
          d="M19 1.10815H2C1.17157 1.10815 0.5 1.77973 0.5 2.60815V13.6082C0.5 14.4366 1.17157 15.1082 2 15.1082H19C19.8284 15.1082 20.5 14.4366 20.5 13.6082V2.60815C20.5 1.77973 19.8284 1.10815 19 1.10815Z"
          stroke="black"
          strokeOpacity="0.1"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_172_9690">
        <rect width="21" height="15" fill="white" transform="translate(0 0.608154)" />
      </clipPath>
    </defs>
  </svg>
);

export const EEA_UK_FLAG_ICON = (
  <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.5" width="21" height="15" rx="2" fill="#2E4E9D" />
    <path
      d="M10.194 1.86356C10.2169 1.81711 10.2831 1.81711 10.306 1.86356L10.6028 2.4649C10.6119 2.48335 10.6295 2.49613 10.6499 2.49909L11.3135 2.59552C11.3648 2.60297 11.3852 2.66597 11.3481 2.70212L10.8679 3.1702C10.8532 3.18456 10.8465 3.20525 10.85 3.22552L10.9633 3.88646C10.9721 3.93751 10.9185 3.97645 10.8726 3.95234L10.2791 3.64029C10.2609 3.63072 10.2391 3.63072 10.2209 3.64029L9.62736 3.95234C9.58151 3.97645 9.52792 3.93751 9.53668 3.88646L9.65004 3.22552C9.65351 3.20525 9.64679 3.18456 9.63206 3.1702L9.15186 2.70212C9.11477 2.66597 9.13524 2.60297 9.1865 2.59552L9.85012 2.49909C9.87047 2.49613 9.88807 2.48335 9.89718 2.4649L10.194 1.86356Z"
      fill="#FFC736"
    />
    <path
      d="M10.194 11.8636C10.2169 11.8171 10.2831 11.8171 10.306 11.8636L10.6028 12.4649C10.6119 12.4833 10.6295 12.4961 10.6499 12.4991L11.3135 12.5955C11.3648 12.603 11.3852 12.666 11.3481 12.7021L10.8679 13.1702C10.8532 13.1846 10.8465 13.2052 10.85 13.2255L10.9633 13.8865C10.9721 13.9375 10.9185 13.9764 10.8726 13.9523L10.2791 13.6403C10.2609 13.6307 10.2391 13.6307 10.2209 13.6403L9.62736 13.9523C9.58151 13.9764 9.52792 13.9375 9.53668 13.8865L9.65004 13.2255C9.65351 13.2052 9.64679 13.1846 9.63206 13.1702L9.15186 12.7021C9.11477 12.666 9.13524 12.603 9.1865 12.5955L9.85012 12.4991C9.87047 12.4961 9.88807 12.4833 9.89718 12.4649L10.194 11.8636Z"
      fill="#FFC736"
    />
    <path
      d="M5.19395 6.86356C5.21688 6.81711 5.28312 6.81711 5.30605 6.86356L5.60282 7.4649C5.61193 7.48335 5.62953 7.49613 5.64988 7.49909L6.3135 7.59552C6.36476 7.60297 6.38523 7.66597 6.34814 7.70212L5.86794 8.1702C5.85321 8.18456 5.84649 8.20525 5.84996 8.22552L5.96332 8.88646C5.97208 8.93751 5.91849 8.97645 5.87264 8.95234L5.27908 8.64029C5.26088 8.63072 5.23912 8.63072 5.22092 8.64029L4.62736 8.95234C4.58151 8.97645 4.52792 8.93751 4.53668 8.88646L4.65004 8.22552C4.65351 8.20525 4.64679 8.18456 4.63206 8.1702L4.15186 7.70212C4.11477 7.66597 4.13524 7.60297 4.1865 7.59552L4.85012 7.49909C4.87047 7.49613 4.88807 7.48335 4.89718 7.4649L5.19395 6.86356Z"
      fill="#FFC736"
    />
    <path
      d="M15.194 6.86356C15.2169 6.81711 15.2831 6.81711 15.306 6.86356L15.6028 7.4649C15.6119 7.48335 15.6295 7.49613 15.6499 7.49909L16.3135 7.59552C16.3648 7.60297 16.3852 7.66597 16.3481 7.70212L15.8679 8.1702C15.8532 8.18456 15.8465 8.20525 15.85 8.22552L15.9633 8.88646C15.9721 8.93751 15.9185 8.97645 15.8726 8.95234L15.2791 8.64029C15.2609 8.63072 15.2391 8.63072 15.2209 8.64029L14.6274 8.95234C14.5815 8.97645 14.5279 8.93751 14.5367 8.88646L14.65 8.22552C14.6535 8.20525 14.6468 8.18456 14.6321 8.1702L14.1519 7.70212C14.1148 7.66597 14.1352 7.60297 14.1865 7.59552L14.8501 7.49909C14.8705 7.49613 14.8881 7.48335 14.8972 7.4649L15.194 6.86356Z"
      fill="#FFC736"
    />
    <path
      d="M12.694 2.53251C12.7169 2.48605 12.7831 2.48605 12.806 2.53251L13.1028 3.13384C13.1119 3.15229 13.1295 3.16508 13.1499 3.16803L13.8135 3.26446C13.8648 3.27191 13.8852 3.33491 13.8481 3.37107L13.3679 3.83915C13.3532 3.8535 13.3465 3.87419 13.35 3.89447L13.4633 4.5554C13.4721 4.60646 13.4185 4.64539 13.3726 4.62129L12.7791 4.30924C12.7609 4.29966 12.7391 4.29966 12.7209 4.30924L12.1274 4.62129C12.0815 4.64539 12.0279 4.60646 12.0367 4.5554L12.15 3.89447C12.1535 3.87419 12.1468 3.8535 12.1321 3.83915L11.6519 3.37107C11.6148 3.33491 11.6352 3.27191 11.6865 3.26446L12.3501 3.16803C12.3705 3.16508 12.3881 3.15229 12.3972 3.13384L12.694 2.53251Z"
      fill="#FFC736"
    />
    <path
      d="M7.69395 11.1946C7.71688 11.1482 7.78312 11.1482 7.80605 11.1946L8.10282 11.796C8.11193 11.8144 8.12953 11.8272 8.14988 11.8301L8.8135 11.9266C8.86476 11.934 8.88523 11.997 8.84814 12.0332L8.36794 12.5013C8.35321 12.5156 8.34649 12.5363 8.34996 12.5566L8.46332 13.2175C8.47208 13.2686 8.41849 13.3075 8.37264 13.2834L7.77908 12.9713C7.76088 12.9618 7.73912 12.9618 7.72092 12.9713L7.12736 13.2834C7.08151 13.3075 7.02792 13.2686 7.03668 13.2175L7.15004 12.5566C7.15351 12.5363 7.14679 12.5156 7.13206 12.5013L6.65186 12.0332C6.61477 11.997 6.63524 11.934 6.6865 11.9266L7.35012 11.8301C7.37047 11.8272 7.38807 11.8144 7.39718 11.796L7.69395 11.1946Z"
      fill="#FFC736"
    />
    <path
      d="M5.86388 4.36356C5.8868 4.31711 5.95304 4.31711 5.97597 4.36356L6.27275 4.9649C6.28185 4.98335 6.29945 4.99613 6.3198 4.99909L6.98342 5.09552C7.03468 5.10297 7.05515 5.16597 7.01806 5.20212L6.53786 5.6702C6.52313 5.68456 6.51641 5.70525 6.51989 5.72552L6.63325 6.38646C6.642 6.43751 6.58841 6.47645 6.54256 6.45234L5.94901 6.14029C5.9308 6.13072 5.90905 6.13072 5.89084 6.14029L5.29728 6.45234C5.25143 6.47645 5.19784 6.43751 5.2066 6.38646L5.31996 5.72552C5.32343 5.70525 5.31671 5.68456 5.30198 5.6702L4.82179 5.20212C4.78469 5.16597 4.80516 5.10297 4.85642 5.09552L5.52004 4.99909C5.5404 4.99613 5.55799 4.98335 5.5671 4.9649L5.86388 4.36356Z"
      fill="#FFC736"
    />
    <path
      d="M14.524 9.36356C14.547 9.31711 14.6132 9.31711 14.6361 9.36356L14.9329 9.9649C14.942 9.98335 14.9596 9.99613 14.98 9.99909L15.6436 10.0955C15.6948 10.103 15.7153 10.166 15.6782 10.2021L15.198 10.6702C15.1833 10.6846 15.1766 10.7052 15.18 10.7255L15.2934 11.3865C15.3022 11.4375 15.2486 11.4764 15.2027 11.4523L14.6092 11.1403C14.591 11.1307 14.5692 11.1307 14.551 11.1403L13.9574 11.4523C13.9116 11.4764 13.858 11.4375 13.8668 11.3865L13.9801 10.7255C13.9836 10.7052 13.9769 10.6846 13.9621 10.6702L13.4819 10.2021C13.4448 10.166 13.4653 10.103 13.5166 10.0955L14.1802 9.99909C14.2006 9.99613 14.2182 9.98335 14.2273 9.9649L14.524 9.36356Z"
      fill="#FFC736"
    />
    <path
      d="M14.524 4.36356C14.547 4.31711 14.6132 4.31711 14.6361 4.36356L14.9329 4.9649C14.942 4.98335 14.9596 4.99613 14.98 4.99909L15.6436 5.09552C15.6948 5.10297 15.7153 5.16597 15.6782 5.20212L15.198 5.6702C15.1833 5.68456 15.1766 5.70525 15.18 5.72552L15.2934 6.38646C15.3022 6.43751 15.2486 6.47645 15.2027 6.45234L14.6092 6.14029C14.591 6.13072 14.5692 6.13072 14.551 6.14029L13.9574 6.45234C13.9116 6.47645 13.858 6.43751 13.8668 6.38646L13.9801 5.72552C13.9836 5.70525 13.9769 5.68456 13.9621 5.6702L13.4819 5.20212C13.4448 5.16597 13.4653 5.10297 13.5166 5.09552L14.1802 4.99909C14.2006 4.99613 14.2182 4.98335 14.2273 4.9649L14.524 4.36356Z"
      fill="#FFC736"
    />
    <path
      d="M5.86388 9.36356C5.8868 9.31711 5.95304 9.31711 5.97597 9.36356L6.27275 9.9649C6.28185 9.98335 6.29945 9.99613 6.3198 9.99909L6.98342 10.0955C7.03468 10.103 7.05515 10.166 7.01806 10.2021L6.53786 10.6702C6.52313 10.6846 6.51641 10.7052 6.51989 10.7255L6.63325 11.3865C6.642 11.4375 6.58841 11.4764 6.54256 11.4523L5.94901 11.1403C5.9308 11.1307 5.90905 11.1307 5.89084 11.1403L5.29728 11.4523C5.25143 11.4764 5.19784 11.4375 5.2066 11.3865L5.31996 10.7255C5.32343 10.7052 5.31671 10.6846 5.30198 10.6702L4.82179 10.2021C4.78469 10.166 4.80516 10.103 4.85642 10.0955L5.52004 9.99909C5.5404 9.99613 5.55799 9.98335 5.5671 9.9649L5.86388 9.36356Z"
      fill="#FFC736"
    />
    <path
      d="M7.69395 2.53251C7.71688 2.48605 7.78312 2.48605 7.80605 2.53251L8.10282 3.13384C8.11193 3.15229 8.12953 3.16508 8.14988 3.16803L8.8135 3.26446C8.86476 3.27191 8.88523 3.33491 8.84814 3.37107L8.36794 3.83915C8.35321 3.8535 8.34649 3.87419 8.34996 3.89447L8.46332 4.5554C8.47208 4.60646 8.41849 4.64539 8.37264 4.62129L7.77908 4.30924C7.76088 4.29966 7.73912 4.29966 7.72092 4.30924L7.12736 4.62129C7.08151 4.64539 7.02792 4.60646 7.03668 4.5554L7.15004 3.89447C7.15351 3.87419 7.14679 3.8535 7.13206 3.83915L6.65186 3.37107C6.61477 3.33491 6.63524 3.27191 6.6865 3.26446L7.35012 3.16803C7.37047 3.16508 7.38807 3.15229 7.39718 3.13384L7.69395 2.53251Z"
      fill="#FFC736"
    />
    <path
      d="M12.694 11.1946C12.7169 11.1482 12.7831 11.1482 12.806 11.1946L13.1028 11.796C13.1119 11.8144 13.1295 11.8272 13.1499 11.8301L13.8135 11.9266C13.8648 11.934 13.8852 11.997 13.8481 12.0332L13.3679 12.5013C13.3532 12.5156 13.3465 12.5363 13.35 12.5566L13.4633 13.2175C13.4721 13.2686 13.4185 13.3075 13.3726 13.2834L12.7791 12.9713C12.7609 12.9618 12.7391 12.9618 12.7209 12.9713L12.1274 13.2834C12.0815 13.3075 12.0279 13.2686 12.0367 13.2175L12.15 12.5566C12.1535 12.5363 12.1468 12.5156 12.1321 12.5013L11.6519 12.0332C11.6148 11.997 11.6352 11.934 11.6865 11.9266L12.3501 11.8301C12.3705 11.8272 12.3881 11.8144 12.3972 11.796L12.694 11.1946Z"
      fill="#FFC736"
    />
  </svg>
);

export const CANADA_FLAG_ICON = (
  <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_188_13912)">
      <mask
        id="mask0_188_13912"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="21"
        height="16"
      >
        <path
          d="M19 0.5H2C0.89543 0.5 0 1.39543 0 2.5V13.5C0 14.6046 0.89543 15.5 2 15.5H19C20.1046 15.5 21 14.6046 21 13.5V2.5C21 1.39543 20.1046 0.5 19 0.5Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_188_13912)">
        <path
          d="M19 0.5H2C0.89543 0.5 0 1.39543 0 2.5V13.5C0 14.6046 0.89543 15.5 2 15.5H19C20.1046 15.5 21 14.6046 21 13.5V2.5C21 1.39543 20.1046 0.5 19 0.5Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 0.5H5V15.5H0V0.5ZM16 0.5H21V15.5H16V0.5ZM10.5 4.5L9.5 6.5L8 5.5V7.5L7 8.5L9 9.5H10V11.5H11V9.5H12L14 8.5L13 7.5V5.5L11.4 6.5L10.5 4.5Z"
          fill="#FF0000"
        />
        <path
          d="M19 1H2C1.17157 1 0.5 1.67157 0.5 2.5V13.5C0.5 14.3284 1.17157 15 2 15H19C19.8284 15 20.5 14.3284 20.5 13.5V2.5C20.5 1.67157 19.8284 1 19 1Z"
          stroke="black"
          strokeOpacity="0.1"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_188_13912">
        <rect width="21" height="15" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
);

export const ProcessingRegionToFlagIcon: { [key in ProcessingRegion]: React.ReactNode } = {
  UNITED_STATES: USA_FLAG_ICON,
  EEA_UK: EEA_UK_FLAG_ICON,
  CANADA: CANADA_FLAG_ICON,
};

const ProcessingRegionToCountryName: { [key in ProcessingRegion]: string } = {
  UNITED_STATES: "USA",
  EEA_UK: "EEA/UK",
  CANADA: "Canada",
};
